import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import api from '../../../api/axios';

const CallDetailPane = ({ isOpen, onClose, callData, onTagsChange, onReviewChange }) => {
  const [showEmailThread, setShowEmailThread] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tags, setTags] = useState(callData?.tags || []);
  const [reviewStatus, setReviewStatus] = useState(callData?.reviewStatus || 'NOT_MARKED');

  useEffect(() => {
    setTags(callData?.tags || []);
    setReviewStatus(callData?.reviewStatus || 'NOT_MARKED');
  }, [callData]);

  if (!isOpen || !callData) return null;

  const handleAddTag = async () => {
    if (!newTag?.trim()) return;
    try {
      const response = await api.post(`/api/v1/conversations/${callData?.conversationId}/tags`, {
        tags: [newTag.trim()]
      });
      if (response.data?.success) {
        const addedTags = response.data?.data || [];
        const updatedTags = [...tags, ...addedTags];
        setTags(updatedTags);
        onTagsChange?.(callData?.conversationId, updatedTags);
      }
    } catch (err) {
      console.error('Error adding tag:', err);
    } finally {
      setNewTag('');
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (!tagToRemove?.id) return;
    try {
      const response = await api.delete(`/api/v1/conversations/${callData?.conversationId}/tags`, {
        data: { tagIds: [tagToRemove.id] }
      });
      if (response.data?.success) {
        const updatedTags = tags?.filter(tag => tag?.id !== tagToRemove.id);
        setTags(updatedTags);
        onTagsChange?.(callData?.conversationId, updatedTags);
      }
    } catch (err) {
      console.error('Error removing tag:', err);
    }
  };

  const emailThreadData = [
    {
      from: 'support@company.com',
      to: callData?.customerName,
      subject: `Re: ${callData?.concernCategory} - ${callData?.id}`,
      date: new Date(callData?.dateTime)?.toLocaleString(),
      body: 'Thank you for contacting us. We have reviewed your case and are working on a resolution.'
    },
    {
      from: callData?.customerName,
      to: 'support@company.com',
      subject: `Re: ${callData?.concernCategory} - ${callData?.id}`,
      date: new Date(new Date(callData?.dateTime).getTime() + 3600000)?.toLocaleString(),
      body: 'Thank you for the quick response. Looking forward to the resolution.'
    }
  ];

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[400] transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-full w-full md:w-[600px] lg:w-[700px] bg-card border-l border-border z-[500] overflow-y-auto shadow-2xl transform transition-transform">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Icon name="FileText" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground">Call Detail View</h2>
                <p className="text-xs md:text-sm text-muted-foreground">{callData?.callId}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-base"
              aria-label="Close panel"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={16} className="text-primary" />
              Call Metadata
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Order ID</p>
                <p className="text-sm font-medium text-foreground">{callData?.metadata?.orderId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer Name</p>
                <p className="text-sm font-medium text-foreground">{callData?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer Type</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  callData?.metadata?.customerType === 'New' ? 'bg-success/10 text-success' :
                  callData?.metadata?.customerType === 'Repeat'? 'bg-primary/10 text-primary' : 'bg-warning/10 text-warning'
                }`}>
                  {callData?.metadata?.customerType}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Human Escalation</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  callData?.metadata?.humanEscalation === 'Yes' ? 'bg-error/10 text-error' : 'bg-success/10 text-success'
                }`}>
                  <Icon name={callData?.metadata?.humanEscalation === 'Yes' ? 'AlertCircle' : 'CheckCircle2'} size={12} />
                  {callData?.metadata?.humanEscalation}
                </span>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Tickets Raised</p>
                {callData?.metadata?.ticketsRaised?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {callData?.metadata?.ticketsRaised?.map((ticket, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-mono">
                        {ticket}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No tickets raised</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="MessageSquare" size={16} className="text-primary" />
                Conversation Thread
              </h3>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {callData?.conversation?.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message?.sender === 'customer' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message?.sender === 'customer' ?'bg-muted border border-border' :'bg-primary text-primary-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold">
                        {message?.sender === 'customer' ? 'Customer' : 'Agent'}
                      </span>
                      <span className="text-xs opacity-70">{message?.time}</span>
                    </div>
                    <p className="text-sm">{message?.text}</p>
                    {/* {message?.sentiment && (
                      <div className="mt-1">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
                          message?.sentiment === 'positive' ? 'bg-success/20 text-success' :
                          message?.sentiment === 'negative'? 'bg-error/20 text-error' : 'bg-muted/20 text-muted-foreground'
                        }`}>
                          <Icon name={message?.sentiment === 'positive' ? 'ThumbsUp' : message?.sentiment === 'negative' ? 'ThumbsDown' : 'Minus'} size={10} />
                          {message?.sentiment}
                        </span>
                      </div>
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Mail" size={16} className="text-primary" />
                Email Communication
              </h3>
              <Button
                variant={callData?.metadata?.emailSent ? 'default' : 'outline'}
                size="sm"
                iconName="Mail"
                iconPosition="left"
                onClick={() => setShowEmailThread(!showEmailThread)}
                className={callData?.metadata?.emailSent ? '' : 'opacity-50'}
              >
                View Email Thread
              </Button>
            </div>
            {showEmailThread && callData?.metadata?.emailSent && (
              <div className="mt-3 space-y-3 max-h-[300px] overflow-y-auto">
                {emailThreadData?.map((email, index) => (
                  <div key={index} className="bg-card rounded-lg p-3 border border-border">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-xs font-semibold text-foreground">From: {email?.from}</p>
                        <p className="text-xs text-muted-foreground">To: {email?.to}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{email?.date}</span>
                    </div>
                    <p className="text-xs font-medium text-foreground mb-1">{email?.subject}</p>
                    <p className="text-xs text-muted-foreground">{email?.body}</p>
                  </div>
                ))}
              </div>
            )}
            {!callData?.metadata?.emailSent && (
              <p className="text-xs text-muted-foreground mt-2">No email communication for this call</p>
            )}
          </div>

          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Tag" size={16} className="text-primary" />
              Tags Management
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Existing Tags</p>
                <div className="flex flex-wrap gap-2">
                  {tags?.map((tag) => (
                    <span
                      key={tag?.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                    >
                      {tag?.tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-error transition-base"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Add New Tag</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e?.target?.value)}
                    onKeyDown={(e) => e?.key === 'Enter' && handleAddTag()}
                    placeholder="Enter tag name"
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    onClick={handleAddTag}
                  >
                    Add
                  </Button>
                </div>
              </div>
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Review Status</p>
                <div className="flex gap-2">
                  {[
                    { value: 'NOT_MARKED', label: 'Not Marked', icon: 'Shield', className: 'bg-muted text-muted-foreground border-border' },
                    { value: 'REVIEW_MARKED', label: 'Under Review', icon: 'ShieldAlert', className: 'bg-warning/10 text-warning border-warning/20' },
                    { value: 'REVIEW_COMPLETED', label: 'Reviewed', icon: 'ShieldCheck', className: 'bg-success/10 text-success border-success/20' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={async () => {
                        if (reviewStatus === option.value) return;
                        try {
                          const response = await api.put(
                            `/api/v1/dashboard/call-details/${callData?.callId}/review-status`,
                            null,
                            { params: { status: option.value } }
                          );
                          if (response.data?.success) {
                            setReviewStatus(option.value);
                            onReviewChange?.(callData?.callId, option.value);
                          }
                        } catch (err) {
                          console.error('Error updating review status:', err);
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-base ${
                        reviewStatus === option.value
                          ? option.className
                          : 'bg-background text-muted-foreground border-border hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={option.icon} size={14} />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CallDetailPane;